import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {z as zod} from "zod";
import {iso8601DateTimeZodValidator, uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {
    organizationalUnitValidator,
    type OrganizationalUnit,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnit";
import {
    organizationalUnitTemplateValidator,
    type OrganizationalUnitTemplate,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplate";
import {userValidator, type User} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import {
    userTemplateValidator,
    type UserTemplate,
} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplate";
import {
    type WorkspaceUsersConfiguration,
    workspaceUsersConfigurationValidator,
} from "@vesta/vos-types/modules/workspaceLevel/users/domain/WorkspaceUsersConfiguration";
import {
    userDetailsValidator,
    type UserDetails,
} from "@vesta/vos-types/modules/platformLevel/userDetails/domain/UserDetails";
import {WorkspaceStatus, type Workspace} from "@vesta/vos-types/modules/platformLevel/workspaces/domain/Workspace";

export enum PrivateInviteStatus {
    active = "9563e5f1-63a1-47cb-a65e-e15d249cd9cc",
    changesRequested = "755187ae-9084-4240-80ca-2fa36c624b57",
    draft = "3173768e-b245-4e22-b3ae-ade29e1b18d4",
    expired = "3e668a0c-2e19-40c4-90cc-b71ce8e55f62",
    pending = "2156dffb-f91c-4854-88df-00c8953447ff",
    redeemed = "0c0720bb-1646-4acc-8db5-576da4436d34",
    rejected = "a08d2028-74ec-453a-bd2c-b7467bafcc12",
    revoked = "851ec1e1-d37c-444c-8648-8624085ae985",
}

export type PrivateInviteChannels = {
    email: boolean;
    phoneNumber: boolean;
};

export const privateInviteChannelsValidator: zod.ZodType<PrivateInviteChannels> = zod.strictObject({
    email: zod.boolean(),
    phoneNumber: zod.boolean(),
});

export type PrivateInviteVerificationRemark =
    | {
          type: PrivateInviteStatus.changesRequested;
          message: string;
      }
    | {
          type: PrivateInviteStatus.rejected;
          message: string;
      };

export const privateInviteVerificationRemarkValidator: zod.ZodType<PrivateInviteVerificationRemark> = zod.discriminatedUnion("type", [
    zod.strictObject({
        type: zod.literal(PrivateInviteStatus.changesRequested),
        message: zod.string(),
    }),
    zod.strictObject({
        type: zod.literal(PrivateInviteStatus.rejected),
        message: zod.string(),
    }),
]);

export type PrivateInvite = {
    allowedEmail: string;
    allowedPhoneNumber: string;
    createdAt: Iso8601DateTime;
    createdBy: Uuid;
    expiresAt: Iso8601DateTime | null;
    fixedAdditionalColumnValues: Record<Uuid, unknown>;
    id: Uuid;
    inviteCode: string;
    inviteFormData: Record<Uuid, unknown> | null;
    /**
     * Parent platform user ID (`workspace_users.user_id`), not the workspace user row ID.
     */
    parentUserId: Uuid | null;
    status: PrivateInviteStatus;
    submittedAt: Iso8601DateTime | null;
    submittedBy: Uuid | null;
    updatedAt: Iso8601DateTime;
    updatedBy: Uuid;
    userTemplateId: Uuid;
    verificationRemark: PrivateInviteVerificationRemark | null;
    workspaceId: Uuid;
};

const privateInviteBaseZodSchema = zod.object({
    allowedEmail: zod.string(),
    allowedPhoneNumber: zod.string(),
    createdAt: iso8601DateTimeZodValidator,
    createdBy: uuidZodValidator,
    expiresAt: iso8601DateTimeZodValidator.nullable(),
    fixedAdditionalColumnValues: zod.record(uuidZodValidator, zod.unknown()),
    id: uuidZodValidator,
    inviteCode: zod.string(),
    inviteFormData: zod.record(uuidZodValidator, zod.unknown()).nullable(),
    parentUserId: uuidZodValidator.nullable(),
    status: zod.enum(PrivateInviteStatus),
    submittedAt: iso8601DateTimeZodValidator.nullable(),
    submittedBy: uuidZodValidator.nullable(),
    updatedAt: iso8601DateTimeZodValidator,
    updatedBy: uuidZodValidator,
    userTemplateId: uuidZodValidator,
    verificationRemark: privateInviteVerificationRemarkValidator.nullable(),
    workspaceId: uuidZodValidator,
});

export const privateInviteValidator: zod.ZodType<PrivateInvite> = privateInviteBaseZodSchema;

export type PrivateInviteByInviteCode = PrivateInvite & {
    organizationalUnitTemplates: Array<OrganizationalUnitTemplate>;
    organizationalUnits: Array<OrganizationalUnit>;
    platformUserDetails: Array<UserDetails>;
    userTemplates: Array<UserTemplate>;
    users: Array<User>;
    usersModuleConfiguration: WorkspaceUsersConfiguration;
};

export const privateInviteByInviteCodeValidator: zod.ZodType<PrivateInviteByInviteCode> = privateInviteBaseZodSchema.extend({
    organizationalUnitTemplates: organizationalUnitTemplateValidator.array(),
    organizationalUnits: organizationalUnitValidator.array(),
    platformUserDetails: userDetailsValidator.array(),
    userTemplates: userTemplateValidator.array(),
    usersModuleConfiguration: workspaceUsersConfigurationValidator,
    users: userValidator.array(),
});

export type InvitedWorkspace = Workspace & {
    inviteCode: string;
    inviteId: Uuid;
    inviteStatus: PrivateInviteStatus;
    verificationRemark: PrivateInviteVerificationRemark | null;
};

export const invitedWorkspaceValidator: zod.ZodType<InvitedWorkspace> = zod.object({
    countryId: uuidZodValidator,
    createdAt: iso8601DateTimeZodValidator,
    createdBy: uuidZodValidator,
    displayName: zod.string(),
    id: uuidZodValidator,
    inviteCode: zod.string(),
    inviteId: uuidZodValidator,
    inviteStatus: zod.enum(PrivateInviteStatus),
    status: zod.enum(WorkspaceStatus),
    storageDatabaseId: uuidZodValidator,
    timeZoneId: uuidZodValidator,
    updatedAt: iso8601DateTimeZodValidator,
    updatedBy: uuidZodValidator,
    verificationRemark: privateInviteVerificationRemarkValidator.nullable(),
});
